/**
 * Row controls shared by full-page Custom Tools and Integrations modal list views.
 * Use CustomToolRowMenuPortal inside overflow:hidden containers (e.g. react-bootstrap Modal).
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MoreHorizontal, Pencil, FlaskConical, Trash2 } from 'lucide-react'

const TOOL_MENU_WIDTH_PX = 144 // matches tw-w-36

export const CustomToolToggle: React.FC<{ checked: boolean; onChange: () => void }> = ({
  checked,
  onChange,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={onChange}
    className={`tw-relative tw-inline-flex tw-h-5 tw-w-9 tw-flex-shrink-0 tw-cursor-pointer tw-rounded-full tw-border-2 tw-border-transparent tw-transition-colors tw-duration-200 tw-ease-in-out focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2 ${
      checked ? 'tw-bg-blue-600' : 'tw-bg-gray-200'
    }`}
  >
    <span
      className={`tw-pointer-events-none tw-inline-block tw-h-4 tw-w-4 tw-transform tw-rounded-full tw-bg-white tw-shadow tw-ring-0 tw-transition tw-duration-200 tw-ease-in-out ${
        checked ? 'tw-translate-x-4' : 'tw-translate-x-0'
      }`}
    />
  </button>
)

export const CustomToolRowMenu: React.FC<{
  onEdit: () => void
  onTest: () => void
  onDelete: () => void
}> = ({ onEdit, onTest, onDelete }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <div className="tw-relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="tw-p-1.5 tw-rounded-lg tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer"
      >
        <MoreHorizontal className="tw-w-4 tw-h-4" />
      </button>
      {open && (
        <div className="tw-absolute tw-right-0 tw-top-full tw-mt-1 tw-w-36 tw-rounded-lg tw-bg-white tw-shadow-lg tw-ring-1 tw-ring-black/5 tw-py-1 tw-z-50">
          <button
            type="button"
            onClick={() => {
              onEdit()
              setOpen(false)
            }}
            className="tw-w-full tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-flex tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-cursor-pointer"
          >
            <Pencil className="tw-w-3.5 tw-h-3.5" /> Edit
          </button>
          <button
            type="button"
            onClick={() => {
              onTest()
              setOpen(false)
            }}
            className="tw-w-full tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-flex tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-cursor-pointer"
          >
            <FlaskConical className="tw-w-3.5 tw-h-3.5" /> Test
          </button>
          <div className="tw-border-t tw-border-gray-100 tw-my-1" />
          <button
            type="button"
            onClick={() => {
              onDelete()
              setOpen(false)
            }}
            className="tw-w-full tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-red-600 hover:tw-bg-red-50 tw-flex tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-cursor-pointer"
          >
            <Trash2 className="tw-w-3.5 tw-h-3.5" /> Delete
          </button>
        </div>
      )}
    </div>
  )
}

export const CustomToolRowMenuPortal: React.FC<{
  onEdit: () => void
  onTest: () => void
  onDelete: () => void
}> = ({ onEdit, onTest, onDelete }) => {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      setCoords(null)
      return
    }
    const el = buttonRef.current
    const rect = el.getBoundingClientRect()
    setCoords({
      top: rect.bottom + 4,
      left: rect.right - TOOL_MENU_WIDTH_PX,
    })
  }, [open])

  useEffect(() => {
    if (!open) return
    const updatePosition = () => {
      const el = buttonRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      setCoords({
        top: rect.bottom + 4,
        left: rect.right - TOOL_MENU_WIDTH_PX,
      })
    }
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      if (buttonRef.current?.contains(t) || menuRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const menu =
    open && coords ? (
      <div
        ref={menuRef}
        role="menu"
        className="ph-tool-menu-portal tw-fixed tw-z-[1060] tw-w-36 tw-rounded-lg tw-bg-white tw-shadow-lg tw-ring-1 tw-ring-black/5 tw-py-1"
        style={{ top: coords.top, left: coords.left }}
      >
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            onEdit()
            setOpen(false)
          }}
          className="tw-w-full tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-flex tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-cursor-pointer"
        >
          <Pencil className="tw-w-3.5 tw-h-3.5" /> Edit
        </button>
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            onTest()
            setOpen(false)
          }}
          className="tw-w-full tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-flex tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-cursor-pointer"
        >
          <FlaskConical className="tw-w-3.5 tw-h-3.5" /> Test
        </button>
        <div className="tw-border-t tw-border-gray-100 tw-my-1" />
        <button
          type="button"
          role="menuitem"
          onClick={() => {
            onDelete()
            setOpen(false)
          }}
          className="tw-w-full tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-red-600 hover:tw-bg-red-50 tw-flex tw-items-center tw-gap-2 tw-border-0 tw-bg-transparent tw-cursor-pointer"
        >
          <Trash2 className="tw-w-3.5 tw-h-3.5" /> Delete
        </button>
      </div>
    ) : null

  return (
    <div className="tw-relative">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
        className="tw-p-1.5 tw-rounded-lg tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer"
      >
        <MoreHorizontal className="tw-w-4 tw-h-4" />
      </button>
      {typeof document !== 'undefined' && menu ? createPortal(menu, document.body) : null}
    </div>
  )
}
